const lazyImports = {
  predictiveSearch: () => import('@theme/predictive-search'),
  quickAdd: () => import('@theme/quick-add'),
  cartDrawer: () => import('@theme/cart-drawer'),
  headerMenu: () => import('@theme/header-menu'),
  slideshow: () => import('@theme/slideshow'),
  favorites: () => import('@theme/favorites'),
};

/** @type {Map<string, Promise<unknown>>} */
const modulePromises = new Map();

function loadModule(name) {
  if (modulePromises.has(name)) {
    return modulePromises.get(name);
  }

  const importer = lazyImports[name];
  if (!importer) {
    return Promise.reject(new Error(`Unknown lazy module: ${name}`));
  }

  const promise = importer().catch((error) => {
    console.error(`Failed to load module "${name}":`, error);
    modulePromises.delete(name);
    throw error;
  });

  modulePromises.set(name, promise);
  return promise;
}

const predictiveSearchBound = new WeakSet();
const quickAddBound = new WeakSet();
const cartDrawerBound = new WeakSet();
const headerMenuBound = new WeakSet();
const favoritesBound = new WeakSet();
const observedSlideshows = new WeakSet();

function bindLazyListeners(boundSet, elements, events, loader, options = {}) {
  for (const element of elements) {
    if (!(element instanceof Element) || boundSet.has(element)) continue;

    boundSet.add(element);

    let triggered = false;
    const handler = () => {
      if (triggered) return;
      triggered = true;
      loader();
    };

    for (const eventName of events) {
      element.addEventListener(eventName, handler, { once: true, ...options });
    }
  }
}

const slideshowObserver = new IntersectionObserver((entries, observer) => {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;

    observer.unobserve(entry.target);
    loadModule('slideshow');
  }
}, {
  rootMargin: '200px 0px 200px 0px',
  threshold: 0.1,
});

function primePredictiveSearch(root = document) {
  const elements = root.querySelectorAll('predictive-search-component');
  bindLazyListeners(
    predictiveSearchBound,
    elements,
    ['focusin', 'pointerdown', 'keydown'],
    () => loadModule('predictiveSearch')
  );

  const triggers = root.querySelectorAll('[on\\:click="#search-modal/showDialog"], search-button button');
  bindLazyListeners(
    predictiveSearchBound,
    triggers,
    ['pointerdown', 'click'],
    () => loadModule('predictiveSearch'),
    { passive: true }
  );
}

function primeQuickAdd(root = document) {
  const quickAddComponents = root.querySelectorAll('quick-add-component');
  bindLazyListeners(
    quickAddBound,
    quickAddComponents,
    ['pointerover', 'pointerdown', 'focusin', 'touchstart'],
    () => loadModule('quickAdd'),
    { passive: true }
  );
}

function primeCartDrawer(root = document) {
  const cartComponents = root.querySelectorAll('cart-drawer-component');
  bindLazyListeners(
    cartDrawerBound,
    cartComponents,
    ['pointerover', 'pointerdown', 'focusin', 'touchstart'],
    () => loadModule('cartDrawer'),
    { passive: true }
  );
}

function primeHeaderMenu(root = document) {
  const headerMenus = root.querySelectorAll('header-menu');
  bindLazyListeners(
    headerMenuBound,
    headerMenus,
    ['pointerenter', 'focusin'],
    () => loadModule('headerMenu'),
    { passive: true }
  );
}

function primeFavorites(root = document) {
  const favoriteButtons = root.querySelectorAll('[data-favorite-toggle], [data-favorites-link]');
  bindLazyListeners(
    favoritesBound,
    favoriteButtons,
    ['pointerdown', 'focusin', 'touchstart'],
    () => loadModule('favorites'),
    { passive: true }
  );
}

function primeSlideshows(root = document) {
  const slideshows = root.querySelectorAll('slideshow-component, slideshow-container');
  for (const slideshow of slideshows) {
    if (!(slideshow instanceof Element) || observedSlideshows.has(slideshow)) continue;
    observedSlideshows.add(slideshow);
    slideshowObserver.observe(slideshow);
  }
}

function primeAll(root = document) {
  primePredictiveSearch(root);
  primeQuickAdd(root);
  primeCartDrawer(root);
  primeHeaderMenu(root);
  primeFavorites(root);
  primeSlideshows(root);
}

primeAll();

const SECTION_EVENTS = [
  'shopify:section:load',
  'shopify:section:select',
  'shopify:section:deselect',
  'shopify:block:select',
  'shopify:block:deselect',
  'shopify:section:reorder',
];

for (const eventName of SECTION_EVENTS) {
  document.addEventListener(eventName, (event) => {
    const root = event.target instanceof HTMLElement ? event.target : document;
    primeAll(root);
  });
}

document.addEventListener('cart:update', () => {
  loadModule('cartDrawer');
}, { once: true });

document.addEventListener('keydown', (event) => {
  if (event.metaKey && event.key.toLowerCase() === 'k') {
    loadModule('predictiveSearch');
  }
}, { once: true });

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') return;
  if (!modulePromises.has('favorites')) {
    return;
  }
  // Trigger favorites module if it was waiting for idle work
  loadModule('favorites');
});

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const quickAddTrigger = event.target.closest('[on\\:click="quick-add-component/handleClick"]');
  if (quickAddTrigger && !customElements.get('quick-add-component')) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const originalEvent = event;

    loadModule('quickAdd')
      .then(() => customElements.whenDefined('quick-add-component'))
      .then(() => {
        const host = quickAddTrigger.closest('quick-add-component');
        if (host && typeof host.handleClick === 'function') {
          host.handleClick(originalEvent);
        } else {
          quickAddTrigger.click();
        }
      })
      .catch((error) => {
        console.error('Failed to initialize quick add:', error);
      });

    return;
  }

  const cartTrigger = event.target.closest('cart-drawer-component [on\\:click="/open"]');
  if (cartTrigger && !customElements.get('cart-drawer-component')) {
    event.preventDefault();
    event.stopImmediatePropagation();

    loadModule('cartDrawer')
      .then(() => customElements.whenDefined('cart-drawer-component'))
      .then(() => {
        const drawer = cartTrigger.closest('cart-drawer-component');
        drawer?.open?.();
      })
      .catch((error) => {
        console.error('Failed to open cart drawer:', error);
      });
  }
}, { capture: true });

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const favoritesTrigger = event.target.closest('[data-favorite-toggle], [data-favorites-link]');
  if (favoritesTrigger && !window.t2mdFavorites) {
    event.preventDefault();
    event.stopImmediatePropagation();

    loadModule('favorites')
      .then(() => {
        if (window.t2mdFavorites && typeof window.t2mdFavorites.handleClick === 'function') {
          window.t2mdFavorites.handleClick(event);
        } else {
          requestAnimationFrame(() => favoritesTrigger.click());
        }
      })
      .catch((error) => {
        console.error('Failed to initialize favourites:', error);
      });
  }
}, { capture: true });

// Warm modules on idle when possible to keep interactions snappy
if (typeof window.requestIdleCallback === 'function') {
  window.requestIdleCallback(() => {
    if (!modulePromises.has('favorites')) {
      loadModule('favorites');
    }
  }, { timeout: 2000 });
}
